type Entry = [IDBValidKey, unknown];

export class IndexedDbKV {
  private dbPromise: Promise<IDBDatabase>;

  constructor(
    private readonly dbName: string,
    private readonly storeName: string,
    private readonly version = 1
  ) {
    this.dbPromise = this.open();
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async read<T>(
    op: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readonly');
      const req = op(tx.objectStore(this.storeName));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  private async write(op: (store: IDBObjectStore) => void): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, 'readwrite');
      op(tx.objectStore(this.storeName));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async getMany(): Promise<Entry[]> {
    const keys = await this.keys();
    const values = await this.read<Entry[]>((store) => store.getAll());

    return keys.map((key, index) => [key, values[index]]);
  }

  async get<T = unknown>(key: IDBValidKey): Promise<T | undefined> {
    return this.read<T | undefined>((store) => store.get(key));
  }

  async set(key: IDBValidKey, value: unknown): Promise<void> {
    await this.write((store) => {
      store.put(value, key);
    });
  }

  async setMany(entries: Entry[]): Promise<void> {
    await this.write((store) => {
      entries.forEach(([key, value]) => store.put(value, key));
    });
  }

  async delete(key: IDBValidKey): Promise<void> {
    await this.write((store) => {
      store.delete(key);
    });
  }

  async clear(): Promise<void> {
    await this.write((store) => {
      store.clear();
    });
  }

  async keys(): Promise<IDBValidKey[]> {
    return this.read<IDBValidKey[]>((store) => store.getAllKeys());
  }

  async close(): Promise<void> {
    const db = await this.dbPromise;
    db.close();
  }
}
